import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@devhop/ui";
import { CheckSquare2, DownloadIcon, EyeIcon, XSquare } from "lucide-react";
import type { QCRecordByIdResult } from "@/modules/qc/domain/types/record";
import { AppImage, resolveImageSrc } from "@/shared/ui/AppImage";
import { formatDateTimeLocal } from "@/shared/lib/date-time";

interface QCRecordDetailContentProps {
  record: QCRecordByIdResult;
}

export function QCRecordDetailContent({ record }: QCRecordDetailContentProps) {
  const getStatusBadge = (status: string | undefined) => {
    switch (status) {
      case "pass":
        return <Badge className="bg-green-100 text-green-800">ຜ່ານ</Badge>;
      case "fail":
        return <Badge variant="destructive">ບໍ່ຜ່ານ</Badge>;
      case "pending":
        return <Badge variant="secondary">ກຳລັງກວດສອບ</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getItemStatusIcon = (status: "pass" | "fail") => {
    if (status === "pass") {
      return <CheckSquare2 className="h-5 w-5 text-green-600" />;
    }
    return <XSquare className="h-5 w-5 text-red-600" />;
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      {/* Section 1: ข้อมูลพื้นฐาน */}
      <Card data-tourid="record-detail-basic-info">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>ຂໍ້ມູນບັນທຶກ QC</span>
            {getStatusBadge(record?.overallStatus)}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <h3 className="mb-1 font-medium text-muted-foreground text-sm">
                ມາດຕະຖານ QC
              </h3>
              <p className="text-sm">{record?.qcStandard.name}</p>
            </div>
            <div>
              <h3 className="mb-1 font-medium text-muted-foreground text-sm">
                ຜູ້ກວດສອບ
              </h3>
              <p className="text-sm">{record?.inspector.name}</p>
            </div>
            <div>
              <h3 className="mb-1 font-medium text-muted-foreground text-sm">
                ວັນທີກວດສອບ
              </h3>
              <p className="text-sm">
                {formatDateTimeLocal(record?.inspectedAt || "")}
              </p>
            </div>
            <div>
              <h3 className="mb-1 font-medium text-muted-foreground text-sm">
                ສະຖານທີ່
              </h3>
              <p className="text-sm">{record?.location || "-"}</p>
            </div>
            {record?.workItemType && (
              <div>
                <h3 className="mb-1 font-medium text-muted-foreground text-sm">
                  ປະເພດງານ
                </h3>
                <p className="text-sm">{record?.workItemType.name}</p>
              </div>
            )}
            {record?.workItemId && (
              <div>
                <h3 className="mb-1 font-medium text-muted-foreground text-sm">
                  ລະຫັດງານ
                </h3>
                <p className="text-sm">{record?.workItemId}</p>
              </div>
            )}
            <div>
              <h3 className="mb-1 font-medium text-muted-foreground text-sm">
                ວັນທີສ້າງ
              </h3>
              <p className="text-sm">
                {formatDateTimeLocal(record?.createdAt || "")}
              </p>
            </div>
            <div>
              <h3 className="mb-1 font-medium text-muted-foreground text-sm">
                ວັນທີອັບເດດລ່າສຸດ
              </h3>
              <p className="text-sm">
                {formatDateTimeLocal(record?.updatedAt || "")}
              </p>
            </div>
          </div>

          {record?.notes && (
            <div>
              <h3 className="mb-1 font-medium text-muted-foreground text-sm">
                ຫມາຍເຫດ
              </h3>
              <p className="whitespace-pre-wrap text-sm leading-relaxed">
                {record?.notes}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section 2: รายการตรวจ */}
      {record?.items && record?.items.length > 0 && (
        <Card data-tourid="record-detail-items">
          <CardHeader>
            <CardTitle className="text-lg">ລາຍການກວດສອບ</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {record?.items.map((item) => (
                <div
                  key={item.id}
                  className="flex items-start gap-3 rounded-lg border p-4"
                >
                  <div className="mt-0.5 flex-shrink-0">
                    {getItemStatusIcon(item.status)}
                  </div>
                  <div className="flex-1 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <h4 className="font-medium text-sm">
                        {item.standardItem.title}
                      </h4>
                      {getStatusBadge(item.status)}
                    </div>
                    {item.standardItem.description && (
                      <p className="text-muted-foreground text-sm">
                        {item.standardItem.description}
                      </p>
                    )}
                    {item.notes && (
                      <div>
                        <h5 className="mb-1 font-medium text-muted-foreground text-xs">
                          ຫມາຍເຫດ:
                        </h5>
                        <p className="whitespace-pre-wrap text-sm">
                          {item.notes}
                        </p>
                      </div>
                    )}
                    {item.photoUrl && (
                      <div>
                        <h5 className="mb-1 font-medium text-muted-foreground text-xs">
                          ຮູບພາບ:
                        </h5>
                        <div className="mt-2 space-y-2">
                          <div className="relative inline-block max-w-xs">
                            <AppImage
                              src={item.photoUrl}
                              alt={item.standardItem.title}
                              className="max-h-64 max-w-xs rounded-lg border"
                              fit="contain"
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const imageUrl = resolveImageSrc(item.photoUrl);
                                if (!imageUrl) return;
                                window.open(imageUrl, "_blank");
                              }}
                              className="gap-1"
                            >
                              <EyeIcon className="h-3 w-3" />
                              ເບິ່ງ
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const imageUrl = resolveImageSrc(item.photoUrl);
                                if (!imageUrl) return;
                                const link = document.createElement("a");
                                link.href = imageUrl;
                                link.download = `${item.standardItem.title}-${item.id}.jpg`;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                              }}
                              className="gap-1"
                            >
                              <DownloadIcon className="h-3 w-3" />
                              ດາວໂຫຼດ
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                    {item.checkedAt && (
                      <p className="text-muted-foreground text-xs">
                        ກວດສອບເມື່ອ:{" "}
                        {item.checkedAt ? formatDateTimeLocal(item.checkedAt) : "-"}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
